
import { useEffect, useRef } from 'react';
import { useThreadStore } from '@/store/thread.store';
import { useOrganizationStore } from '@/store/organization.store';
import { useRealtime } from '@/lib/realtime/core/useRealtime';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { SocketEvent } from '@scrambl/shared';
import {
  MEETING_RECORDINGS_KEY,
  type MeetingRecordingUpdatePayload,
} from '@/hooks/useMeetingRecordings';
import { useGlobalModuleEventFallback } from '@/lib/realtime/features/useGlobalModuleEventFallback';

import RealTimeSync from './RealTimeSync';
/**
 * Component that initializes realtime listeners at the application level
 * Uses the new centralized realtime system exclusively
 */
export function RealTimeInitializer() {
  const { threads } = useThreadStore();
  const { currentOrganization } = useOrganizationStore();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isThreadSurface =
    location.pathname.includes('/threads') ||
    location.pathname.includes('/discussions');

  // Use the realtime hook for room management
  const {
    isReady,
    batchJoinThreads,
    batchLeaveThreads,
    joinRoom,
    socket,
  } = useRealtime({
    suppressLogging: false,
  });

  // App-level fallback for recording pipeline events: useMeetingRecordings
  // patches the cache surgically, but its listener only exists while a
  // recordings surface is mounted. Mark the affected queries invalid here
  // (without forcing a refetch of mounted ones — the page hook handles those)
  // so the recordings/notes pages refetch on their next mount instead of
  // showing a stale list until the next window-focus refetch.
  useEffect(() => {
    if (!socket || !isReady) {
      return;
    }
    const listener = (payload: MeetingRecordingUpdatePayload) => {
      void queryClient.invalidateQueries({
        queryKey: MEETING_RECORDINGS_KEY,
        refetchType: 'none',
      });
      if (payload.status === 'READY') {
        // A READY recording created a note — mark note lists too
        void queryClient.invalidateQueries({
          queryKey: ['notes'],
          refetchType: 'none',
        });
      }
    };
    socket.on(SocketEvent.MEETING_RECORDING_UPDATED, listener);
    return () => {
      socket.off(SocketEvent.MEETING_RECORDING_UPDATED, listener);
    };
  }, [socket, isReady, queryClient]);

  // App-level freshness fallback for every module whose create/update/delete
  // events only have page-scoped listeners — marks their caches invalid so
  // closed pages refetch on next mount (see the hook for the full rationale).
  useGlobalModuleEventFallback();

  // Track if we've already joined rooms to prevent duplicate joins
  const joinedRoomsRef = useRef(false);

  // Join organization room when organization is available and realtime is ready
  useEffect(() => {
    if (!isReady || !currentOrganization?.id) {
      return;
    }

    // Join the organization room for org-wide events
    joinRoom('organization', currentOrganization.id);

    // No cleanup needed as the socket disconnection will handle it
  }, [isReady, currentOrganization?.id, joinRoom]);

  // Batch join thread rooms when threads are loaded
  useEffect(() => {
    if (
      !isThreadSurface ||
      !isReady ||
      !currentOrganization?.id ||
      threads.length === 0 ||
      joinedRoomsRef.current
    ) {
      return;
    }

    // Extract thread IDs for batch joining
    const threadIds = threads.map(thread => thread.id);

    // Set flag to prevent joining rooms multiple times
    joinedRoomsRef.current = true;

    // Use the centralized batchJoinThreads method
    batchJoinThreads(threadIds, currentOrganization.id);

    return () => {
      // Leave thread rooms when unmounting
      if (threadIds.length > 0) {
        batchLeaveThreads(threadIds);

        // Reset join flag
        joinedRoomsRef.current = false;
      }
    };
  }, [
    isThreadSurface,
    isReady,
    currentOrganization?.id,
    threads,
    batchJoinThreads,
    batchLeaveThreads,
  ]);

  // Include RealTimeSync component for cross-tab synchronization
  return <RealTimeSync />;
}

export default RealTimeInitializer;
